import { useQuery } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import type { MerchantCategoryRestrictionsState } from 'src/feature/UpdatedPhysicalCardSettings/UpdatedPhysicalCardSettings'
import { mccGroupsQueries } from 'src/queries/mccGroups.queries'
import type {
  CapabilityDecisionResponse,
  ReplaceCardManagementMccRestrictionsRequest,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { isCardManagementViewAvailable } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  physicalCardManagementQueries,
  useReplaceCardManagementMccRestrictions,
} from 'src/queries/physicalCardManagement/physicalCardManagement.queries'
import { createIdempotencyKey } from 'src/utils/idempotency.utils'

type MerchantCategoryRestrictionsStateInput = {
  enabled: boolean
  creditCardId?: string
}

type MccSaveAttempt = {
  fingerprint: string
  idempotencyKey: string
}

type MccCapabilityDecision = Extract<
  CapabilityDecisionResponse,
  { action: 'MANAGE_PHYSICAL_CARD_MCC' }
>

const isMccCapabilityDecision = (
  capability: CapabilityDecisionResponse,
): capability is MccCapabilityDecision =>
  capability.action === 'MANAGE_PHYSICAL_CARD_MCC'

const errorMessage = (error: unknown): string | undefined => {
  if (
    error !== null &&
    typeof error === 'object' &&
    'error' in error &&
    typeof error.error === 'string'
  ) {
    return error.error
  }
  return error instanceof Error ? error.message : undefined
}

export const useMerchantCategoryRestrictionsState = ({
  enabled,
  creditCardId,
}: MerchantCategoryRestrictionsStateInput): MerchantCategoryRestrictionsState => {
  const capabilitiesQuery = useQuery({
    ...physicalCardManagementQueries.capabilities(creditCardId),
    enabled: enabled && Boolean(creditCardId),
  })
  const manageCapability = capabilitiesQuery.data?.capabilities.find(
    isMccCapabilityDecision,
  )
  // Shared predicate rather than an inline find: the same demo-enrollment
  // verdict now also gates the card-detail hero, and two call sites disagreeing
  // about whether READ_ONLY counts would be a silent divergence.
  const canView = isCardManagementViewAvailable(
    capabilitiesQuery.data?.capabilities,
  )
  const canManage =
    canView &&
    manageCapability?.availability === 'AVAILABLE' &&
    manageCapability.execution === 'DIRECT' &&
    manageCapability.constraints !== null
  const shouldLoadMcc = enabled && Boolean(creditCardId) && canView
  const catalogueQuery = useQuery({
    ...mccGroupsQueries.cardManagementCatalogue(),
    enabled: shouldLoadMcc,
  })
  const restrictionsQuery = useQuery({
    ...physicalCardManagementQueries.mccRestrictions(creditCardId),
    enabled: shouldLoadMcc,
  })
  const replaceMutation = useReplaceCardManagementMccRestrictions()
  const attemptRef = useRef<MccSaveAttempt>()
  const unknownOutcome = replaceMutation.error?.errorCode === 'CARD-MGMT.13'

  const onSave = useCallback(
    async (
      request: ReplaceCardManagementMccRestrictionsRequest,
    ): Promise<void> => {
      if (!creditCardId || !canManage) {
        throw new Error('MCC restrictions are not manageable for this card')
      }
      if (unknownOutcome) {
        throw new Error('Reload MCC restrictions before trying to save again')
      }

      const fingerprint = JSON.stringify({
        mode: request.mode,
        exceptionMccGroupIds: [...request.exceptionMccGroupIds].sort(),
        expectedVersion: request.expectedVersion,
      })
      const attempt =
        attemptRef.current?.fingerprint === fingerprint
          ? attemptRef.current
          : {
              fingerprint,
              idempotencyKey: createIdempotencyKey('card-management-mcc'),
            }
      attemptRef.current = attempt

      await replaceMutation.mutateAsync({
        creditCardId,
        request,
        idempotencyKey: attempt.idempotencyKey,
      })
      attemptRef.current = undefined
    },
    [canManage, creditCardId, replaceMutation, unknownOutcome],
  )

  const onReload = useCallback(async (): Promise<void> => {
    const [catalogueResult, restrictionsResult] = await Promise.all([
      catalogueQuery.refetch(),
      restrictionsQuery.refetch(),
    ])
    const reloadError = catalogueResult.error ?? restrictionsResult.error
    if (reloadError) {
      throw reloadError
    }
    attemptRef.current = undefined
    replaceMutation.reset()
  }, [catalogueQuery, replaceMutation, restrictionsQuery])

  return {
    catalogue: catalogueQuery.data ?? [],
    restrictions: restrictionsQuery.data,
    canView,
    canManage,
    manageReasonCode: manageCapability?.reasonCode,
    manageRequirements: manageCapability?.constraints ?? undefined,
    isLoading:
      capabilitiesQuery.isLoading ||
      (shouldLoadMcc &&
        (catalogueQuery.isLoading || restrictionsQuery.isLoading)),
    loadError:
      errorMessage(capabilitiesQuery.error) ??
      (shouldLoadMcc
        ? (errorMessage(catalogueQuery.error) ??
          errorMessage(restrictionsQuery.error))
        : undefined),
    isSaving: replaceMutation.isPending,
    saveError: errorMessage(replaceMutation.error),
    saveErrorCode: replaceMutation.error?.errorCode,
    unknownOutcome,
    onSave,
    onReload,
  }
}
